import { Link } from "react-router-dom";
import { SectionLabel } from "@/components/motion";
import { CONTACT } from "@/lib/data";

const SECTIONS = [
  {
    title: "1. Who We Are",
    body: `ELT Freight & Logistics ("ELT", "we", "us") is an independent freight brokerage and logistics coordination company headquartered at ${CONTACT.address}, ${CONTACT.city}. We operate across Canada and United States cross-border markets. This Privacy Policy explains how we collect, use, disclose and protect personal information in accordance with Canada's Personal Information Protection and Electronic Documents Act (PIPEDA), Alberta's Personal Information Protection Act (PIPA), and applicable United States federal and state privacy laws.`,
  },
  {
    title: "2. Information We Collect",
    list: [
      "Quote requests: contact name, company, email, phone, origin/destination, freight details, equipment needs and timing.",
      "Carrier applications: owner/contact name, company, email, phone, equipment type, fleet size, operating area, operating authority numbers (MC / DOT / NSC) and availability.",
      "Contact messages: name, email, subject and message content.",
      "Technical data: standard server logs such as IP address, browser type and pages visited, used for security and performance only.",
    ],
  },
  {
    title: "3. How We Use Your Information",
    list: [
      "To source, quote, book, coordinate and track freight transportation.",
      "To evaluate, vet and onboard carriers and owner-operators.",
      "To communicate with you about shipments, rates, paperwork and proof of delivery.",
      "To maintain records required for our business operations, accounting and regulatory compliance in Canada and the United States.",
      "To respond to enquiries and improve our services.",
    ],
  },
  {
    title: "4. Consent",
    body: "By submitting a form on this website or otherwise providing personal information to us, you consent to its collection, use and disclosure as described in this policy. You may withdraw consent at any time, subject to legal and contractual restrictions, by contacting us at the address or email below.",
  },
  {
    title: "5. Disclosure to Third Parties",
    body: "We share shipment and contact details with carriers, owner-operators and service providers strictly as needed to coordinate your freight, and with regulators, insurers, customs authorities or law enforcement where required by law (including Canadian and U.S. transportation and border authorities). We do not sell, rent or trade personal information.",
  },
  {
    title: "6. Cross-Border Processing",
    body: "Because we coordinate Canada/U.S. cross-border freight, your information may be processed or stored in either country. When transferred across the border, information remains subject to this policy and to the laws of the jurisdiction where it is processed.",
  },
  {
    title: "7. Retention & Security",
    body: "We retain personal information only as long as necessary for the purposes described above and to meet legal, tax and regulatory record-keeping requirements. We use reasonable administrative, technical and physical safeguards — including access controls and encrypted transport (TLS) — to protect information against loss, theft and unauthorized access.",
  },
  {
    title: "8. Your Rights (Canada & U.S.)",
    list: [
      "Canada (PIPEDA / Alberta PIPA): you may request access to, or correction of, your personal information and may challenge our compliance.",
      "United States: depending on your state of residence (e.g., California under the CCPA/CPRA), you may have rights to know, access, correct, delete or opt out of certain processing. We do not sell or share personal information for cross-context behavioural advertising.",
      "To exercise any right, contact us using the details below. We will respond within the time required by applicable law.",
    ],
  },
  {
    title: "9. Cookies & Analytics",
    body: "This website does not use advertising trackers. Any analytics used are limited to aggregate performance measurement. You can control cookies through your browser settings.",
  },
  {
    title: "10. Changes to This Policy",
    body: "We may update this policy from time to time. The effective date above reflects the latest revision. Material changes will be posted on this page.",
  },
];

export default function Privacy() {
  return (
    <main className="pt-16" data-testid="privacy-page">
      <section className="border-b border-line">
        <div className="mx-auto max-w-4xl px-5 py-20 lg:px-8">
          <SectionLabel>// Legal</SectionLabel>
          <h1 className="mt-3 font-display text-4xl font-black uppercase leading-[1.05] tracking-tight text-bright sm:text-5xl">
            Privacy <span className="text-hazard">Policy</span>
          </h1>
          <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-dim">Effective: October 2026 — Applies in Canada &amp; the United States</p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 py-16 lg:px-8">
        <div className="flex flex-col gap-8">
          {SECTIONS.map((s) => (
            <div key={s.title} className="border border-line bg-panel p-7" data-testid={`privacy-section-${s.title.split(".")[0]}`}>
              <h2 className="font-display text-xl font-bold uppercase text-bright">{s.title}</h2>
              {s.body && <p className="mt-3 text-sm leading-relaxed text-[#9CA3AF]">{s.body}</p>}
              {s.list && (
                <ul className="mt-3 flex flex-col gap-2">
                  {s.list.map((item) => (
                    <li key={item} className="flex gap-3 text-sm leading-relaxed text-[#9CA3AF]">
                      <span className="mt-2 h-1 w-1 shrink-0 rotate-45 bg-hazard" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          <div className="border border-hazard/40 bg-panel p-7" data-testid="privacy-contact">
            <h2 className="font-display text-xl font-bold uppercase text-bright">Privacy Contact</h2>
            <p className="mt-3 text-sm leading-relaxed text-[#9CA3AF]">
              ELT Freight &amp; Logistics — Privacy Officer<br />
              {CONTACT.address}<br />
              {CONTACT.city}
            </p>
            <a href={`mailto:${CONTACT.dispatch}`} data-testid="privacy-email-link" className="mt-3 inline-block font-mono text-sm text-hazard hover:underline">
              {CONTACT.dispatch}
            </a>
          </div>

          <p className="text-sm text-dim">
            See also our <Link to="/terms" data-testid="privacy-terms-link" className="text-hazard hover:underline">Terms of Service</Link>.
          </p>
        </div>
      </section>
    </main>
  );
}
